(() => {
  'use strict';

  const GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  const SCROLL_TRIGGER_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';
  const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.149.0/build/three.min.js';
  const GLTF_URL = 'https://cdn.jsdelivr.net/npm/three@0.149.0/examples/js/loaders/GLTFLoader.js';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  const loadScript = (src) => new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve();
      else existing.addEventListener('load', resolve, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.addEventListener('load', () => { script.dataset.loaded = 'true'; resolve(); }, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });

  const cssNumber = (element, property, fallback = 0) => {
    const value = parseFloat(getComputedStyle(element).getPropertyValue(property));
    return Number.isFinite(value) ? value : fallback;
  };

  function setupParticles(section) {
    section.querySelectorAll('[data-particles] i').forEach((particle, index) => {
      const seed = index + 1;
      particle.style.left = `${(seed * 37 + seed * seed * 3) % 100}%`;
      particle.style.top = `${(seed * 53 + seed * seed * 7) % 100}%`;
      const size = 1 + (seed % 3);
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.opacity = String(0.18 + (seed % 5) * 0.1);
      particle.style.animationDuration = `${8 + (seed % 6) * 2}s`;
      particle.style.animationDelay = `${seed * -0.35}s`;
    });
  }

  function updateFocusCard(section, product) {
    const card = section.querySelector('[data-hero-product-card]');
    if (!card || !product) return;
    card.querySelector('[data-focus-title]').textContent = product.dataset.title || 'Produit LMDS';
    card.querySelector('[data-focus-benefit]').textContent = product.dataset.benefit || '';
    const link = product.dataset.link;
    if (link) card.href = link;
  }

  function initNavigation(section) {
    const nav = section.querySelector('[data-scroll-nav]');
    const mobileProgress = section.querySelector('[data-mobile-scroll-progress]');
    if (!nav && !mobileProgress) return () => {};
    const links = [...(nav?.querySelectorAll('[data-scroll-nav-item]') || [])];
    const targets = links.map((link) => document.querySelector(link.getAttribute('href'))).filter(Boolean);
    const progress = nav?.querySelector('[data-scroll-progress]');
    const faq = document.querySelector('#lmds-faq');
    let raf = 0;

    const updatePageProgress = () => {
      raf = 0;
      const start = section.getBoundingClientRect().top + window.scrollY;
      const endTarget = faq || targets[targets.length - 1] || section;
      const end = endTarget.getBoundingClientRect().bottom + window.scrollY - window.innerHeight * 0.4;
      const ratio = Math.min(1, Math.max(0, (window.scrollY - start) / Math.max(1, end - start)));
      if (progress) progress.style.transform = `scaleY(${ratio})`;
      if (nav) nav.classList.toggle('is-hidden', window.scrollY < start - 80 || window.scrollY > end + window.innerHeight * 0.45);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(updatePageProgress); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updatePageProgress();

    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      links.forEach((link) => link.classList.toggle('is-active', link.getAttribute('href') === `#${visible.target.id}`));
    }, { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.2, 0.55] });
    targets.forEach((target) => observer.observe(target));

    links.forEach((link) => link.addEventListener('click', (event) => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }));

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }

  async function initWebGL(section, products, renderCallbacks) {
    const enabled = section.dataset.webgl === 'true';
    const capable = window.innerWidth >= 760 && (navigator.hardwareConcurrency || 4) >= 4 && (!navigator.deviceMemory || navigator.deviceMemory >= 4);
    if (!enabled || !capable || reduceMotion.matches || !products.some((product) => product.dataset.modelUrl)) return;
    try {
      await loadScript(THREE_URL);
      await loadScript(GLTF_URL);
    } catch (_) {
      return;
    }
    if (!window.THREE?.GLTFLoader) return;

    products.forEach((product) => {
      const modelUrl = product.dataset.modelUrl;
      const canvas = product.querySelector('[data-product-canvas]');
      if (!modelUrl || !canvas) return;
      const width = Math.max(220, product.clientWidth);
      const height = Math.max(220, product.clientHeight);
      const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
      renderer.setSize(width, height, false);
      renderer.outputEncoding = THREE.sRGBEncoding;
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(33, width / height, 0.01, 100);
      camera.position.set(0, 0, 4.2);
      scene.add(new THREE.HemisphereLight(0xddeeff, 0x101820, 2.1));
      const key = new THREE.DirectionalLight(0xffffff, 2.7);
      key.position.set(3, 4, 5);
      scene.add(key);
      const rim = new THREE.DirectionalLight(0x168bff, 2.2);
      rim.position.set(-4, 1, -2);
      scene.add(rim);

      new THREE.GLTFLoader().load(modelUrl, (gltf) => {
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        model.position.sub(center);
        const fit = 2.5 / Math.max(size.x, size.y, size.z, 0.001);
        model.scale.setScalar(fit * Number(product.dataset.modelScale || 1));
        model.rotation.y = Number(product.dataset.modelRotation || 0) * Math.PI / 180;
        scene.add(model);
        renderer.render(scene, camera);
        product.classList.add('is-webgl');
        renderCallbacks.set(product, (progress) => {
          model.rotation.y = (Number(product.dataset.modelRotation || 0) * Math.PI / 180) + progress * 0.32;
          model.rotation.x = Math.sin(progress * Math.PI) * 0.04;
          renderer.render(scene, camera);
        });
      }, undefined, () => {
        renderer.dispose();
      });
    });
  }

  async function initSection(section) {
    if (!section || section.dataset.heroReady === 'true') return;
    section.dataset.heroReady = 'true';
    setupParticles(section);
    const cleanNavigation = initNavigation(section);
    const products = [...section.querySelectorAll('[data-hero-product]')];
    const viewport = section.querySelector('[data-hero-viewport]');
    const scene = section.querySelector('[data-product-scene]');
    const copy = section.querySelector('[data-hero-copy]');
    const card = section.querySelector('[data-hero-product-card]');
    const ring = section.querySelector('.hero-3d__orbital-ring');
    const mobileProgress = section.querySelector('[data-mobile-scroll-progress]');
    if (!viewport || !scene || !products.length) return;

    const maxMobile = Number(section.dataset.maxMobileProducts || 4);
    products.forEach((product, index) => product.classList.toggle('is-mobile-trimmed', index >= maxMobile));
    updateFocusCard(section, products.find((product) => product.dataset.primary === 'true') || products[0]);

    if (reduceMotion.matches) {
      products.forEach((product) => { product.style.opacity = '1'; });
      instances.set(section, { destroy: cleanNavigation });
      return;
    }

    try {
      await loadScript(GSAP_URL);
      await loadScript(SCROLL_TRIGGER_URL);
    } catch (_) {
      products.forEach((product) => { product.style.opacity = '1'; });
      instances.set(section, { destroy: cleanNavigation });
      return;
    }

    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    const renderCallbacks = new Map();
    initWebGL(section, products, renderCallbacks);

    const base = products.map((product) => ({
      xVw: cssNumber(product, window.innerWidth <= 800 ? '--x-mobile' : '--x-desktop'),
      yVh: cssNumber(product, window.innerWidth <= 800 ? '--y-mobile' : '--y-desktop'),
      z: cssNumber(product, '--depth'),
      scale: cssNumber(product, '--scale', 1),
      rotationStart: cssNumber(product, '--rotation-start'),
      rotationEnd: cssNumber(product, '--rotation-end'),
      travelY: cssNumber(product, '--travel-y'),
      parallax: cssNumber(product, '--product-parallax', 1)
    }));
    const positions = () => base.map((item) => ({ ...item, x: window.innerWidth * item.xVw / 100, y: window.innerHeight * item.yVh / 100 }));
    let coords = positions();
    const primaryIndex = Math.max(0, products.findIndex((product) => product.dataset.primary === 'true'));
    const focusIndex = products.length > 1 ? (primaryIndex === 0 ? 1 : 0) : primaryIndex;
    const thirdIndex = products.length > 2 ? 2 : focusIndex;

    products.forEach((product, index) => {
      const item = coords[index];
      gsap.set(product, { xPercent: -50, yPercent: -50, x: item.x, y: item.y, z: item.z, scale: item.scale, rotationZ: item.rotationStart, opacity: 0, transformPerspective: 1600, transformOrigin: '50% 50%' });
    });
    gsap.to(products, { opacity: 1, duration: 1.1, stagger: 0.08, ease: 'power3.out', delay: 0.15 });
    gsap.from(copy, { opacity: 0, y: 28, duration: 1, ease: 'power3.out', delay: 0.15 });

    const timeline = gsap.timeline({ defaults: { ease: 'none' } });
    const primary = products[primaryIndex];
    const focus = products[focusIndex];
    const third = products[thirdIndex];
    const p = coords[primaryIndex];
    const f = coords[focusIndex];
    const t = coords[thirdIndex];

    timeline.to(primary, { z: p.z + 210, scale: p.scale * 1.12, rotationZ: p.rotationEnd, y: p.y + p.travelY * .35, duration: .42 }, 0);
    products.forEach((product, index) => {
      if (index === primaryIndex) return;
      const item = coords[index];
      const direction = item.x >= 0 ? 1 : -1;
      timeline.to(product, { x: item.x + direction * 55 * item.parallax, y: item.y + item.travelY * .45, z: item.z - 85 * item.parallax, rotationZ: item.rotationEnd, duration: .42 }, 0);
    });
    timeline.to(copy, { opacity: 0, y: -70, duration: .2 }, .22);
    timeline.to(card, { y: -15, scale: 1.03, duration: .18 }, .26);
    timeline.to(primary, { x: p.x + window.innerWidth * .08, y: p.y - 35, z: p.z - 30, scale: p.scale * .84, opacity: .72, duration: .28 }, .43);
    timeline.to(focus, { x: window.innerWidth * .22, y: -window.innerHeight * .01, z: 170, scale: Math.max(f.scale * 1.28, .95), rotationZ: f.rotationEnd, opacity: 1, duration: .28 }, .43);
    timeline.to(focus, { rotationZ: f.rotationEnd + 6, y: -35, duration: .18 }, .62);
    timeline.to(focus, { x: window.innerWidth * .31, z: -20, scale: f.scale * .82, opacity: .52, duration: .22 }, .76);
    timeline.to(third, { x: window.innerWidth * .18, y: -20, z: 125, scale: Math.max(t.scale * 1.18, .85), opacity: 1, duration: .18 }, .73);
    timeline.to(products, { y: (index) => coords[index].y - 100, z: (index) => coords[index].z - 170, scale: (index) => coords[index].scale * .72, opacity: .28, duration: .2 }, .86);
    if (ring) timeline.to(ring, { scale: .78, opacity: .22, duration: .2 }, .82);
    timeline.to(card, { opacity: 0, y: -35, duration: .15 }, .86);

    const scrollLength = Number(section.dataset.scrollLength || 320);
    const trigger = ScrollTrigger.create({
      animation: timeline,
      trigger: section,
      start: 'top top',
      end: () => `+=${window.innerHeight * scrollLength / 100}`,
      pin: viewport,
      pinSpacing: true,
      scrub: Number(section.dataset.animationSpeed || 1) * .65,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const progress = self.progress;
        const product = progress < .42 ? primary : progress < .75 ? focus : third;
        if (section._activeFocus !== product) {
          section._activeFocus = product;
          updateFocusCard(section, product);
        }
        if (mobileProgress) mobileProgress.style.transform = `scaleX(${progress})`;
        renderCallbacks.forEach((render) => render(progress));
      }
    });

    let mouseRaf = 0;
    const onPointer = (event) => {
      if (window.innerWidth < 900) return;
      if (mouseRaf) cancelAnimationFrame(mouseRaf);
      mouseRaf = requestAnimationFrame(() => {
        const intensity = Number(section.dataset.parallax || 1);
        const mx = (event.clientX / window.innerWidth - .5) * 2;
        const my = (event.clientY / window.innerHeight - .5) * 2;
        gsap.to(scene, { x: mx * 12 * intensity, y: my * 9 * intensity, rotationY: mx * 1.2 * intensity, rotationX: -my * .7 * intensity, duration: .7, overwrite: 'auto' });
      });
    };
    viewport.addEventListener('pointermove', onPointer, { passive: true });

    const onResize = () => {
      coords = positions();
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', onResize);

    const destroy = () => {
      cleanNavigation();
      viewport.removeEventListener('pointermove', onPointer);
      window.removeEventListener('resize', onResize);
      trigger.kill(true);
      timeline.kill();
      gsap.killTweensOf([products, copy, card, scene]);
      section.dataset.heroReady = 'false';
      instances.delete(section);
    };
    instances.set(section, { destroy });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-hero-3d]').forEach((section) => initSection(section));
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector?.('[data-hero-3d]') || (event.target.matches?.('[data-hero-3d]') ? event.target : null);
    instances.get(section)?.destroy();
  });
})();
